import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ControlledDropzone,
  ControlledNumberField,
  ControlledSelect,
  ControlledTextarea,
  ControlledTextField,
} from "../../../components/form";
import {
  Discount,
  ProductFormData,
  ProductFormProps,
} from "../types/products-types";
import { productSchema } from "../../../schemas/forms";
import { ProductStatus } from "../../../types";

export const ProductForm: React.FC<ProductFormProps> = ({
  onSubmit,
  initialValues,
  isLoading = false,
  brands,
  models,
  selectedBrandId: initialSelectedBrandId,
}) => {
  // State for managing removed images
  const [removedMainImage, setRemovedMainImage] = useState<string | null>(null);
  const [removedImages, setRemovedImages] = useState<string[]>([]);

  // State for managing discounts
  const [discounts, setDiscounts] = useState<Discount[]>(() => {
    // Initialize with existing discounts if editing
    const hasDiscount = initialValues?.rental?.hasDiscount;
    const discountType = initialValues?.rental?.discountType as
      | "HOURS"
      | "DAYS"
      | undefined;
    const discountDuration = initialValues?.rental?.discountDuration;
    const discountAmount = (initialValues as any)?.discountAmount ?? 0;

    if (hasDiscount || (discountAmount ?? 0) > 0) {
      return [
        {
          id: "1",
          type: discountType || "HOURS",
          duration:
            typeof discountDuration === "number" && discountDuration > 0
              ? discountDuration
              : 1,
          price: Number(discountAmount) || 0,
        },
      ];
    }
    return [];
  });

  // Helper function to find brand/model ID by name (for editing existing products)
  const findBrandIdByName = (brandName: string): string => {
    const brand = brands.find((b) => b.name === brandName);
    return brand?.id || "";
  };

  const findModelIdByName = (modelName: string): string => {
    const model = models.find((m) => m.name === modelName);
    return model?.id || "";
  };

  // Helper function to ensure we have a valid brand ID (not name)
  const getValidBrandId = (value: string | undefined): string => {
    if (!value) return "";

    // Check if it's already an ID (find by ID)
    const brandById = brands.find((b) => b.id === value);
    if (brandById) return value;

    // Check if it's a name (find by name and return ID)
    const brandByName = brands.find((b) => b.name === value);
    return brandByName?.id || "";
  };

  // Initialize form with default values
  const form = useForm<ProductFormData, any, ProductFormData>({
    resolver: zodResolver(productSchema as any),
    defaultValues: {
      lotNumber: initialValues?.lotNumber || "",
      brand:
        initialValues?.brandId ||
        (initialValues?.brand
          ? findBrandIdByName(initialValues.brand)
          : getValidBrandId(initialSelectedBrandId)),
      model:
        initialValues?.modelId ||
        (initialValues?.model ? findModelIdByName(initialValues.model) : ""),
      brandId: initialValues?.brandId || "",
      modelId: initialValues?.modelId || "",
      year: Number(initialValues?.year || new Date().getFullYear()),
      mileage: Number(initialValues?.mileage || 0),
      fuelType: initialValues?.fuelType || "GASOLINE",
      transmission: initialValues?.transmission || "MANUAL",
      drivetrain: initialValues?.drivetrain || "FWD",
      engineSize: Number(initialValues?.engineSize || 1.0),
      licensePlate: initialValues?.licensePlate || "",
      color: initialValues?.color || "",
      location: initialValues?.location || "",
      status: initialValues?.status || "ACTIVE",
      rentalStatus: initialValues?.rental?.status || "ACTIVE",
      rentalPeriod: initialValues?.rental?.rentalPeriod || "DAY",
      price: Number(initialValues?.rental?.price || initialValues?.price || 0),
      hasDiscount: initialValues?.rental?.hasDiscount || false,
      discountCreatedAt: initialValues?.rental?.discountCreatedAt || null,
      discountType: initialValues?.rental?.discountType || "HOURS",
      discountDuration: Number(initialValues?.rental?.discountDuration || 0),
      discountAmount: Number((initialValues as any)?.discountAmount || 0),
      mainImage: "", // Clear form field when editing - use existingFiles prop instead
      images: [], // Clear form field when editing - use existingFiles prop instead
      description: initialValues?.description || "",
      order: Number(initialValues?.order || 0),
      isActive: initialValues?.isActive ?? true,
    },
  });

  // Get the selected brand ID from form values
  const selectedBrandId = form.watch("brand");

  // Filter models based on selected brand
  const filteredModels = selectedBrandId
    ? models.filter((model) => model.brandId === selectedBrandId)
    : [];

  // Reset model field when brand changes (only if not editing existing product with same brand)
  useEffect(() => {
    if (selectedBrandId && !initialValues) {
      form.setValue("model", "");
    } else if (
      selectedBrandId &&
      initialValues &&
      selectedBrandId !== initialValues.brandId
    ) {
      // Reset model if brand changed while editing
      form.setValue("model", "");
    }
  }, [selectedBrandId, form, initialValues]);

  // Watch rentalStatus and sync it to status
  const rentalStatus = form.watch("rentalStatus");
  useEffect(() => {
    if (rentalStatus) {
      form.setValue("status", rentalStatus as ProductStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentalStatus]);

  // Discount management functions
  const addDiscount = () => {
    if (discounts.length === 0) {
      const newDiscount: Discount = {
        id: Date.now().toString(),
        type: "HOURS",
        duration: 1,
        price: 0,
      };
      setDiscounts([newDiscount]);
      // Sync with form state
      form.setValue("hasDiscount", true);
      form.setValue("discountType", "HOURS");
      form.setValue("discountDuration", 1 as any);
      form.setValue("discountAmount", 0 as any);
      form.setValue("discountCreatedAt", new Date().toISOString());
    }
  };

  const removeDiscount = () => {
    setDiscounts([]);
    // Sync with form state
    form.setValue("hasDiscount", false);
    form.setValue("discountDuration", 0 as any);
    form.setValue("discountAmount", 0 as any);
    form.setValue("discountCreatedAt", null);
  };

  const handleFormSubmit = async (data: ProductFormData): Promise<void> => {
    // Helper function to safely convert to number, handling edge cases
    const safeNumber = (value: any): number => {
      // Handle null, undefined, empty string, or invalid values
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === "-"
      ) {
        return 0;
      }
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };

    // Find the selected brand and model objects to get their names
    const selectedBrand = brands.find((brand) => brand.id === data.brand);
    const selectedModel = models.find((model) => model.id === data.model);

    // Generate lotNumber from licensePlate if not provided
    const generatedLotNumber = data.lotNumber || `LOT${data.licensePlate}`;

    // Read discount values from controlled form fields
    const formDiscountType = data.discountType;
    const formDiscountDuration = safeNumber(data.discountDuration as any);

    // Determine mainImage URL (prioritize new upload, fallback to existing)
    const mainImageUrl = removedMainImage
      ? data.mainImage || ""
      : data.mainImage || initialValues?.mainImage || "";

    // Convert price to number and log it
    const priceValue = safeNumber(data.price);

    // Map frontend form data to backend expected format with proper type conversion
    const values: any = {
      name: `${selectedBrand?.name || ""} ${selectedModel?.name || ""} ${
        data.year
      } ${generatedLotNumber}`.trim(), // Backend expects 'name' field
      brandId: data.brand, // Backend expects brandId (database ID)
      modelId: data.model, // Backend expects modelId (database ID)
      mileage: safeNumber(data.mileage),
      engineSize: safeNumber(data.engineSize),
      year: safeNumber(data.year),
      fuelType: data.fuelType,
      transmission: data.transmission,
      drivetrain: data.drivetrain,
      licensePlate: data.licensePlate,
      color: data.color,
      lotNumber: generatedLotNumber,
      rental: {
        status: data.rentalStatus, // Use the rental status from form
        rentalPeriod: data.rentalPeriod,
        price: priceValue, // Already converted to safe number
        hasDiscount: discounts.length > 0,
        discountType:
          discounts.length > 0 ? formDiscountType || "HOURS" : "HOURS",
        discountDuration:
          discounts.length > 0 ? safeNumber(formDiscountDuration) : 0, // Safely convert to Number
        discountAmount:
          discounts.length > 0 ? safeNumber(data.discountAmount) : 0, // Safely convert to Number
        discountCreatedAt:
          discounts.length > 0
            ? data.discountCreatedAt || new Date().toISOString()
            : null,
      },
      images: [
        // Keep existing images that weren't removed
        ...(initialValues?.images || []).filter(
          (img) => !removedImages.includes(img)
        ),
        // Add new images from form (already URLs from ControlledDropzone)
        ...(Array.isArray(data.images) ? data.images : []),
      ],
      mainImage: mainImageUrl, // Already a string URL from ControlledDropzone
      description: data.description || "",
      order: safeNumber(data.order), // Safely convert to Number
      meta: {
        title: "",
        description: "",
        keywords: "",
      },
      isActive: data.isActive,
      isFeatured: false,
      // Use status from form data
      status: data.status,
    };

    try {
      await onSubmit(values);
    } catch (error) {
      throw error; // Re-throw to let the parent component handle it
    }
  };

  return (
    <form
      onSubmit={form.handleSubmit(handleFormSubmit, (errors) => {
        console.log(errors);
      })}
      className="form-space-y-2"
    >
      <ControlledSelect
        control={form.control}
        name="brand"
        label="Бренд"
        placeholder="Выберите бренд"
        disabled={isLoading}
        rules={{ required: "Бренд обязателен" }}
        options={[
          ...(brands?.map((brand) => ({
            value: brand.id,
            label: brand.name,
          })) || []),
        ]}
      />

      <ControlledSelect
        control={form.control}
        name="model"
        label="Модель"
        placeholder={
          selectedBrandId ? "Выберите модель" : "Сначала выберите бренд"
        }
        disabled={isLoading || !selectedBrandId}
        rules={{ required: "Модель обязательна" }}
        options={[
          ...(filteredModels?.map((model) => ({
            value: model.id,
            label: model.name,
          })) || []),
        ]}
      />

      <div className="form-row">
        <ControlledNumberField
          control={form.control}
          name="year"
          label="Год"
          min={1900}
          max={new Date().getFullYear() + 1}
          disabled={isLoading}
          allowDecimals={false}
          rules={{
            required: "Год обязателен",
            min: { value: 1900, message: "Год должен быть не менее 1900" },
            max: {
              value: new Date().getFullYear() + 1,
              message: "Год не может быть больше текущего + 1",
            },
          }}
        />

        <ControlledNumberField
          control={form.control}
          name="mileage"
          label="Пробег (км)"
          min={0}
          disabled={isLoading}
          allowDecimals={false}
          rules={{
            required: "Пробег обязателен",
            min: { value: 0, message: "Пробег не может быть отрицательным" },
          }}
        />
      </div>

      <div className="form-row">
        <ControlledSelect
          control={form.control}
          name="fuelType"
          label="Тип топлива"
          disabled={isLoading}
          rules={{ required: "Тип топлива обязателен" }}
          options={[
            { value: "GASOLINE", label: "Бензин" },
            { value: "DIESEL", label: "Дизель" },
            { value: "ELECTRIC", label: "Электрический" },
            { value: "HYBRID", label: "Гибридный" },
            { value: "LPG", label: "Газ" },
          ]}
        />

        <ControlledSelect
          control={form.control}
          name="transmission"
          label="Коробка передач"
          disabled={isLoading}
          rules={{ required: "Коробка передач обязательна" }}
          options={[
            { value: "MANUAL", label: "МКПП" },
            { value: "AUTOMATIC", label: "АКПП" },
            { value: "CVT", label: "Вариатор" },
            { value: "SEMI_AUTOMATIC", label: "РКПП" },
          ]}
        />
      </div>

      <div className="form-row">
        <ControlledSelect
          control={form.control}
          name="drivetrain"
          label="Привод"
          disabled={isLoading}
          rules={{ required: "Привод обязателен" }}
          options={[
            { value: "FWD", label: "Передний" },
            { value: "RWD", label: "Задний" },
            { value: "AWD", label: "Полный" },
            { value: "4WD", label: "4WD" },
          ]}
        />

        <ControlledNumberField
          control={form.control}
          name="engineSize"
          label="Объем двигателя (л)"
          min={0.1}
          max={10}
          step={0.1}
          disabled={isLoading}
          allowDecimals={true}
          rules={{
            required: "Объем двигателя обязателен",
            min: { value: 0.1, message: "Объем должен быть не менее 0.1" },
            max: { value: 10, message: "Объем должен быть не более 10" },
          }}
        />
      </div>

      <div className="form-row">
        <ControlledTextField
          control={form.control}
          name="licensePlate"
          label="Госномер"
          placeholder="Введите госномер"
          disabled={isLoading}
        />

        <ControlledTextField
          control={form.control}
          name="color"
          label="Цвет кузова"
          placeholder="Введите цвет"
          disabled={isLoading}
        />
      </div>

      <ControlledSelect
        control={form.control}
        name="rentalStatus"
        // status car
        label="Статус автомобиля"
        placeholder="Выберите статус"
        disabled={isLoading}
        rules={{ required: "Статус автомобиля обязателен" }}
        options={[
          { value: "ACTIVE", label: "Активный" },
          { value: "RENTING", label: "В аренде" },
          { value: "ARCHIVED", label: "Архивный" },
        ]}
      />

      <div className="form-section">
        <h3>Аренда</h3>
        <div className="form-row">
          <ControlledSelect
            control={form.control}
            name="rentalPeriod"
            label="Период аренды"
            disabled={isLoading}
            rules={{ required: "Период аренды обязателен" }}
            options={[
              { value: "HOUR", label: "Час" },
              { value: "DAY", label: "День" },
              { value: "WEEK", label: "Неделя" },
              { value: "MONTH", label: "Месяц" },
            ]}
          />
          <ControlledNumberField
            control={form.control}
            name="price"
            label="Цена аренды (₽)"
            min={0}
            disabled={isLoading}
            allowDecimals={false}
            rules={{
              required: "Цена аренды обязательна",
              min: { value: 0, message: "Цена не может быть отрицательной" },
            }}
          />
        </div>

        {/* Single Discount Section */}
        <div className="form-group">
          <div className="discounts-header">
            <label>Скидка</label>
            {discounts.length === 0 ? (
              <button
                type="button"
                onClick={addDiscount}
                className="add-discount-btn"
                disabled={isLoading}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Добавить скидку
              </button>
            ) : (
              <button
                type="button"
                onClick={removeDiscount}
                className="add-discount-btn"
                disabled={isLoading}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Удалить скидку
              </button>
            )}
          </div>
        </div>

        {discounts.length > 0 && discounts[0] && (
          <div className="discount-item">
            <div className="discount-item-header">
              <h4>Скидка</h4>
            </div>
            <div className="form-row">
              <ControlledSelect
                control={form.control}
                name="discountType"
                label="Тип скидки"
                disabled={isLoading}
                options={[
                  { value: "HOURS", label: "Часы" },
                  { value: "DAYS", label: "Дни" },
                ]}
              />

              <ControlledNumberField
                control={form.control}
                name="discountDuration"
                label="Длительность"
                min={1}
                disabled={isLoading}
                allowDecimals={false}
                rules={{
                  required: "Длительность обязательна",
                  min: { value: 1, message: "Минимум 1" },
                }}
              />

              <ControlledNumberField
                control={form.control}
                name="discountAmount"
                label="Цена скидки (₽)"
                min={0}
                disabled={isLoading}
                allowDecimals={false}
                rules={{
                  required: "Цена скидки обязательна",
                  min: { value: 0, message: "Не может быть отрицательной" },
                }}
              />
            </div>
          </div>
        )}
      </div>

      <ControlledDropzone
        control={form.control}
        name="mainImage"
        label="Главное изображение"
        disabled={isLoading}
        maxFiles={1}
        maxSizePerFile={5}
        multiple={false}
        autoUpload={true}
        uploadFolder="products"
        showPreviews={true}
        accept={{
          "image/*": [".jpeg", ".jpg", ".png", ".webp"],
        }}
        existingFiles={
          initialValues?.mainImage && !removedMainImage
            ? [initialValues.mainImage]
            : []
        }
        onRemoveExisting={(url) => {
          // Handle removal of existing main image
          setRemovedMainImage(url);
        }}
      />

      <ControlledDropzone
        control={form.control}
        name="images"
        label="Дополнительные изображения"
        disabled={isLoading}
        maxFiles={15}
        maxSizePerFile={5}
        multiple={true}
        autoUpload={true}
        uploadFolder="products"
        showPreviews={true}
        accept={{
          "image/*": [".jpeg", ".jpg", ".png", ".webp"],
        }}
        existingFiles={(initialValues?.images || []).filter(
          (img) => !removedImages.includes(img)
        )}
        onRemoveExisting={(url) => {
          // Handle removal of existing images
          setRemovedImages((prev) => [...prev, url]);
        }}
      />

      <ControlledTextarea
        control={form.control}
        name="description"
        label="Описание"
        rows={4}
        placeholder="Введите описание автомобиля"
        disabled={isLoading}
      />

      <div className="form-wrapper__actions">
        <button
          type="submit"
          className="form-button form-button--primary form-button--full"
          disabled={isLoading}
        >
          {isLoading
            ? "Загрузка..."
            : initialValues
            ? "Сохранить изменения"
            : "Добавить продукт"}
        </button>
      </div>
    </form>
  );
};
